import { useState, useMemo } from 'react';

const PRESET_THEMES = [
  { id: 'default', name: 'Default', colors: { bg: '#ffffff', text: '#1f2937', accent: '#3b5998', card: '#f9fafb' } },
  { id: 'cyberpunk', name: 'Cyberpunk', colors: { bg: '#0a0a0c', text: '#e0e0e0', accent: '#ff00ff', card: '#141416' } },
  { id: 'retro', name: 'Retro', colors: { bg: '#fffbeb', text: '#451a03', accent: '#f97316', card: '#fff7ed' } },
  { id: 'matrix', name: 'Matrix', colors: { bg: '#000000', text: '#00ff00', accent: '#00ff00', card: '#0a1a0a' } },
  { id: 'ocean', name: 'Ocean', colors: { bg: '#0c1222', text: '#94a3b8', accent: '#0ea5e9', card: '#1e293b' } },
  { id: 'sunset', name: 'Sunset', colors: { bg: '#1a0a1e', text: '#fca5a5', accent: '#f97316', card: '#2d1a33' } },
];

const DEFAULT_CSS = `/* Custom CSS for your SYMSVOX profile */
/* Edit below to customize your look! */

.profile-header {
  /* background: linear-gradient(135deg, #667eea, #764ba2); */
}

.profile-avatar {
  /* border: 3px solid gold; */
  /* box-shadow: 0 0 20px rgba(255, 215, 0, 0.5); */
}

.profile-name {
  /* font-family: 'Comic Sans MS', cursive; */
  /* text-shadow: 2px 2px 4px rgba(0,0,0,0.5); */
}

.profile-bio {
  /* font-style: italic; */
  /* color: #ffd700; */
}

.profile-stats {
  /* background: rgba(0,0,0,0.3); */
  /* border: 1px solid rgba(255,255,255,0.1); */
}

.badge-grid .badge {
  /* transform: rotate(-5deg); */
  /* transition: transform 0.3s; */
}

.badge-grid .badge:hover {
  /* transform: rotate(0deg) scale(1.2); */
}`;

export function CSSEditorPage() {
  const [css, setCss] = useState(DEFAULT_CSS);
  const [activePreset, setActivePreset] = useState('default');
  const [showPreview, setShowPreview] = useState(true);
  const [saved, setSaved] = useState(false);

  const presetColors = useMemo(() =>
    PRESET_THEMES.find(t => t.id === activePreset)?.colors || PRESET_THEMES[0].colors,
    [activePreset]
  );

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setCss(DEFAULT_CSS);
    setActivePreset('default');
  };

  const lineCount = css.split('\n').length;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="social-box overflow-hidden">
        <div className="box-header flex items-center justify-between">
          <span>Custom CSS Editor</span>
          <div className="flex gap-1">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="text-2xs font-bold px-2 py-0.5 cursor-pointer"
              style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={handleReset}
              className="text-2xs font-bold px-2 py-0.5 cursor-pointer"
              style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              className="text-2xs font-bold px-2 py-0.5 cursor-pointer"
              style={{ background: saved ? '#10b981' : 'var(--accent)', color: 'white', border: `1px solid ${saved ? '#059669' : 'var(--accent-hover)'}` }}
            >
              {saved ? 'Saved!' : 'Save CSS'}
            </button>
          </div>
        </div>

        {/* Preset Themes */}
        <div className="px-3 py-2 flex gap-2 flex-wrap" style={{ borderBottom: '1px solid var(--border-separator)' }}>
          <span className="text-2xs font-bold self-center" style={{ color: 'var(--text-muted)' }}>Presets:</span>
          {PRESET_THEMES.map(theme => (
            <button
              key={theme.id}
              onClick={() => setActivePreset(theme.id)}
              className="flex items-center gap-1 px-2 py-0.5 text-2xs font-bold cursor-pointer"
              style={{
                background: activePreset === theme.id ? 'var(--accent)' : 'var(--bg-filter)',
                color: activePreset === theme.id ? 'white' : 'var(--text-muted)',
                border: '1px solid var(--border-separator)',
              }}
            >
              <span
                className="w-2.5 h-2.5"
                style={{ background: theme.colors.accent, border: '1px solid rgba(255,255,255,0.2)' }}
              />
              {theme.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        {/* Editor */}
        <div className="flex-1 social-box overflow-hidden">
          <div className="flex items-center justify-between px-2 py-1" style={{ background: '#1e1e1e', borderBottom: '1px solid #333' }}>
            <span style={{ color: '#858585', fontSize: '10px' }}>profile.css — {lineCount} lines</span>
            <span style={{ color: '#858585', fontSize: '9px' }}>CSS</span>
          </div>
          <div className="flex" style={{ background: '#1e1e1e' }}>
            {/* Line Numbers */}
            <div className="py-2 px-1 select-none text-right" style={{ color: '#858585', fontSize: '11px', fontFamily: 'monospace', lineHeight: '1.5', minWidth: '30px' }}>
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i}>{i + 1}</div>
              ))}
            </div>
            {/* Editor */}
            <textarea
              value={css}
              onChange={e => setCss(e.target.value)}
              spellCheck={false}
              className="flex-1 p-2 resize-none focus:outline-none"
              style={{
                background: '#1e1e1e',
                color: '#d4d4d4',
                fontFamily: 'Consolas, "Courier New", monospace',
                fontSize: '11px',
                lineHeight: '1.5',
                border: 'none',
                minHeight: '350px',
                tabSize: 2,
              }}
            />
          </div>
        </div>

        {/* Preview */}
        {showPreview && (
          <div className="social-box overflow-hidden" style={{ width: '280px' }}>
            <div className="box-header">Live Preview</div>
            <div className="p-3" style={{ background: presetColors.bg, minHeight: '350px' }}>
              {/* Mini Profile Preview */}
              <div className="profile-header p-3 mb-2" style={{ background: `linear-gradient(135deg, ${presetColors.accent}, ${presetColors.card})`, border: `1px solid ${presetColors.accent}40` }}>
                <div className="flex items-center gap-2">
                  <div
                    className="profile-avatar w-10 h-10 flex items-center justify-center text-lg font-bold"
                    style={{ background: presetColors.accent, color: 'white', border: `2px solid ${presetColors.accent}` }}
                  >
                    J
                  </div>
                  <div>
                    <div className="profile-name font-bold text-xs" style={{ color: 'white' }}>jordan_dev</div>
                    <div className="profile-bio text-2xs" style={{ color: 'rgba(255,255,255,0.7)' }}>Building the future</div>
                  </div>
                </div>
              </div>

              <div className="profile-stats p-2 mb-2" style={{ background: presetColors.card, border: `1px solid ${presetColors.accent}30` }}>
                <div className="flex justify-between text-2xs" style={{ color: presetColors.text }}>
                  <span>Spawns: <strong>47</strong></span>
                  <span>Tasks: <strong>312</strong></span>
                  <span>Level: <strong>4</strong></span>
                </div>
              </div>

              <div className="badge-grid flex gap-1 mb-2">
                {['🎯', '⭐', '🔧', '🚀'].map((b, i) => (
                  <div
                    key={i}
                    className="badge w-7 h-7 flex items-center justify-center text-sm"
                    style={{ background: presetColors.card, border: `1px solid ${presetColors.accent}30` }}
                  >
                    {b}
                  </div>
                ))}
              </div>

              <div className="p-2" style={{ background: presetColors.card, border: `1px solid ${presetColors.accent}30` }}>
                <div className="text-2xs font-bold mb-1" style={{ color: presetColors.text }}>Wall</div>
                <div className="text-2xs" style={{ color: presetColors.text, opacity: 0.7 }}>
                  "Just hit 99.8% uptime on Sym-Agent"
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS Reference */}
      <div className="social-box overflow-hidden">
        <div className="box-header">CSS Class Reference</div>
        <div className="p-2">
          <table className="w-full" style={{ fontSize: '10px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-separator)' }}>
                <th className="text-left py-1 px-2" style={{ color: 'var(--text-muted)' }}>Class</th>
                <th className="text-left py-1 px-2" style={{ color: 'var(--text-muted)' }}>Element</th>
                <th className="text-left py-1 px-2" style={{ color: 'var(--text-muted)' }}>Supports</th>
              </tr>
            </thead>
            <tbody>
              {[
                { cls: '.profile-header', elem: 'Header banner', supports: 'background, border, padding' },
                { cls: '.profile-avatar', elem: 'Avatar image/div', supports: 'border, box-shadow, border-radius' },
                { cls: '.profile-name', elem: 'Display name', supports: 'font, color, text-shadow' },
                { cls: '.profile-bio', elem: 'Bio text', supports: 'font, color, style' },
                { cls: '.profile-stats', elem: 'Stats container', supports: 'background, border, padding' },
                { cls: '.badge-grid .badge', elem: 'Each badge', supports: 'transform, transition, border' },
                { cls: '.profile-wall', elem: 'Wall section', supports: 'background, border, font' },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: i < 6 ? '1px solid var(--border-separator)' : 'none' }}>
                  <td className="py-1 px-2 font-mono font-bold" style={{ color: '#8b5cf6' }}>{row.cls}</td>
                  <td className="py-1 px-2" style={{ color: 'var(--text-secondary)' }}>{row.elem}</td>
                  <td className="py-1 px-2" style={{ color: 'var(--text-muted)' }}>{row.supports}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
